import React, {Suspense} from 'react';
import {
    Button,
    Chip,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Popover,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import menus from "./menus";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import {DrawerHeader} from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import {SIDEBAR_STYLES, SIDEBAR_VARIANTS, SIDEBAR_VIEWS} from "@jumbo/utils/constants/layout";
import Logo from "../../../../shared/Logo";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import {useLocation, useNavigate} from "react-router-dom";
import { ENV_NAME, HOST } from 'app/utils/constants/paths';
import {alpha} from '@mui/material/styles';
import _ from 'lodash';
import userServices from 'app/services/user-services';

const Sidebar = () => {
    const {authUser, setAuthToken} = useJumboAuth();
    const {sidebarOptions, setSidebarOptions} = useJumboLayoutSidebar();
    const navigate = useNavigate();
    const location = useLocation();
    const isDesktop = useMediaQuery('(min-width:1200px)');
    const [companySwitcherAnchorEl, setCompanySwitcherAnchorEl] = React.useState(null);

    const isMiniAndClosed = React.useMemo(() => {
        return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
    }, [sidebarOptions?.view, sidebarOptions?.open]);

    const availableCompanies = React.useMemo(() => {
        return _.filter(authUser?.companies || [], (company) => `${company?.id}` !== `${authUser?.current_company?.id}`);
    }, [authUser?.companies, authUser?.current_company?.id]);

    const canSwitchCompany = availableCompanies.length > 0;
    const companySwitcherOpen = Boolean(companySwitcherAnchorEl);

    const handleOpenCompanySwitcher = (event) => {
        if (!canSwitchCompany) return;
        setCompanySwitcherAnchorEl(event.currentTarget);
    };

    const handleCloseCompanySwitcher = () => {
        setCompanySwitcherAnchorEl(null);
    };

    const handleCompanyChange = async (company) => {
        handleCloseCompanySwitcher();
        if (!company?.id) return;
        try {
            await userServices.setCurrentCompany(company.id);
            window.location.reload();
        } catch (_error) {}
    };

    // Como los ítems del menú: en pantallas chicas el menú se cierra al ir a la página.
    const handleOpenProfile = () => {
        navigate('/profile');
        if (!isDesktop) setSidebarOptions({open: false});
    };

    const profileSelected = location.pathname === '/profile';

    const handleLogout = () => {
        setAuthToken(null);
        navigate('/auth/login');
    };

    const companyName = authUser?.current_company?.name || 'Sin empresa';

    return (
        <React.Fragment>
            <SidebarHeader/>
            <Div sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0}}>
                <JumboScrollbar
                    autoHide
                    autoHideDuration={200}
                    autoHideTimeout={500}
                    style={{flex: 1}}
                >
                    <Suspense
                        fallback={
                            <Div
                                sx={{
                                    display: 'flex',
                                    minWidth: 0,
                                    alignItems: 'center',
                                    alignContent: 'center',
                                    px: 3
                                }}
                            >
                                <SidebarSkeleton/>
                            </Div>
                        }
                    >
                        <JumboVerticalNavbar translate items={menus(authUser)}/>
                    </Suspense>
                    { ENV_NAME !== 'production' &&
                        <Div sx={{px: 2}}>
                            <Chip label={_.upperCase(ENV_NAME)} color="secondary" sx={{ width: '100%' }}/>
                        </Div>
                    }
                </JumboScrollbar>

                <Div
                    sx={{
                        px: isMiniAndClosed ? 0.75 : 1.2,
                        py: 1,
                        borderTop: `1px solid ${alpha('#0F172A', 0.14)}`,
                        backgroundColor: alpha('#FFFFFF', 0.82),
                        backdropFilter: 'blur(4px)',
                    }}
                >
                    <Stack spacing={0.6}>
                        <Tooltip title="Mi perfil" placement="right">
                            <Button
                                fullWidth
                                size="small"
                                color="inherit"
                                onClick={handleOpenProfile}
                                startIcon={<AccountCircleOutlinedIcon fontSize="small"/>}
                                sx={{
                                    minWidth: 0,
                                    px: isMiniAndClosed ? 0.65 : 1,
                                    py: 0.65,
                                    textTransform: 'none',
                                    justifyContent: isMiniAndClosed ? 'center' : 'flex-start',
                                    // Activo, con los colores de un ítem del menú seleccionado.
                                    color: theme => profileSelected ? theme.palette.nav.action.active : alpha('#0F172A', 0.8),
                                    backgroundColor: theme => profileSelected ? theme.palette.nav.background.active : 'transparent',
                                    fontWeight: 600,
                                    '& .MuiButton-startIcon': {
                                        mr: isMiniAndClosed ? 0 : 1,
                                        ml: 0,
                                    },
                                }}>
                                {!isMiniAndClosed && 'Mi perfil'}
                            </Button>
                        </Tooltip>

                        <Tooltip title={companyName} placement="right">
                            <span>
                                <Button
                                    fullWidth
                                    size="small"
                                    color="inherit"
                                    disabled={!canSwitchCompany}
                                    onClick={handleOpenCompanySwitcher}
                                    startIcon={<BusinessOutlinedIcon fontSize="small"/>}
                                    endIcon={!isMiniAndClosed && canSwitchCompany
                                        ? (companySwitcherOpen ? <KeyboardArrowUpOutlinedIcon fontSize="small"/> : <KeyboardArrowDownOutlinedIcon fontSize="small"/>)
                                        : null}
                                    sx={{
                                        minWidth: 0,
                                        px: isMiniAndClosed ? 0.65 : 1,
                                        py: 0.65,
                                        textTransform: 'none',
                                        justifyContent: isMiniAndClosed ? 'center' : 'flex-start',
                                        color: alpha('#0F172A', 0.8),
                                        fontWeight: 600,
                                        '& .MuiButton-startIcon': {
                                            mr: isMiniAndClosed ? 0 : 1,
                                            ml: 0,
                                        },
                                        '& .MuiButton-endIcon': {
                                            ml: 0.6,
                                        },
                                        '&.Mui-disabled': {
                                            color: alpha('#0F172A', 0.56),
                                        },
                                    }}>
                                    {!isMiniAndClosed && (
                                        <Typography variant="body2" noWrap sx={{fontWeight: 600}}>
                                            {companyName}
                                        </Typography>
                                    )}
                                </Button>
                            </span>
                        </Tooltip>

                        <Tooltip title="Cerrar sesión" placement="right">
                            <Button
                                fullWidth
                                size="small"
                                color="inherit"
                                onClick={handleLogout}
                                startIcon={<LogoutIcon fontSize="small"/>}
                                sx={{
                                    minWidth: 0,
                                    px: isMiniAndClosed ? 0.65 : 1,
                                    py: 0.65,
                                    textTransform: 'none',
                                    justifyContent: isMiniAndClosed ? 'center' : 'flex-start',
                                    color: alpha('#B91C1C', 0.9),
                                    fontWeight: 600,
                                    '& .MuiButton-startIcon': {
                                        mr: isMiniAndClosed ? 0 : 1,
                                        ml: 0,
                                    },
                                }}>
                                {!isMiniAndClosed && 'Cerrar sesión'}
                            </Button>
                        </Tooltip>
                    </Stack>
                </Div>
            </Div>

            <Popover
                open={companySwitcherOpen}
                anchorEl={companySwitcherAnchorEl}
                onClose={handleCloseCompanySwitcher}
                anchorOrigin={{vertical: 'top', horizontal: 'left'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'left'}}
            >
                <Stack sx={{py: 0.4, minWidth: 220, maxHeight: 320, overflowY: 'auto'}}>
                    {!availableCompanies.length ? (
                        <Typography sx={{px: 1.5, py: 1}} color="text.secondary">
                            No hay otras empresas
                        </Typography>
                    ) : (
                        availableCompanies.map((company) => (
                            <MenuItem
                                key={`sidebar-company-${company.id}`}
                                onClick={() => handleCompanyChange(company)}
                            >
                                <ListItemIcon sx={{minWidth: 30}}>
                                    <BusinessOutlinedIcon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText primary={company.name}/>
                            </MenuItem>
                        ))
                    )}
                </Stack>
            </Popover>
        </React.Fragment>
    );
};

const SidebarHeader = () => {
    const {sidebarOptions, setSidebarOptions} = useJumboLayoutSidebar();
    const {sidebarTheme} = useJumboSidebarTheme();
    const isDesktop = useMediaQuery('(min-width:1200px)');

    const {authUser} = useJumboAuth();
    const compLogo = authUser?.current_company?.logo_url;
    const compLogoUrl = compLogo ? `${HOST}${compLogo}` : null;

    const isMiniAndClosed = React.useMemo(() => {
        return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
    }, [sidebarOptions.view, sidebarOptions.open]);

    const showSidebarToggle = React.useMemo(() => {
        if (isDesktop) {
            return sidebarOptions?.view !== SIDEBAR_VIEWS.MINI || sidebarOptions?.open;
        }
        return sidebarOptions?.view !== SIDEBAR_VIEWS.MINI;
    }, [isDesktop, sidebarOptions?.view, sidebarOptions?.open]);

    const handleSidebarToggle = React.useCallback(() => {
        if (!isDesktop) {
            setSidebarOptions({open: false});
            return;
        }

        if (sidebarOptions?.view === SIDEBAR_VIEWS.FULL && sidebarOptions?.open) {
            setSidebarOptions({
                view: SIDEBAR_VIEWS.MINI,
                variant: SIDEBAR_VARIANTS.PERMANENT,
                open: false,
            });
            return;
        }

        setSidebarOptions({
            view: SIDEBAR_VIEWS.FULL,
            variant: SIDEBAR_VARIANTS.PERMANENT,
            open: true,
        });
    }, [isDesktop, setSidebarOptions, sidebarOptions]);
    
    return (
        <React.Fragment>
            {
                sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER &&
                <DrawerHeader>
                    <Logo mini={isMiniAndClosed} mode={sidebarTheme.type} logo_url={compLogoUrl} link_sx={{display: 'flex'}} img_style={{maxHeight: '40px'}} />
                    {
                        showSidebarToggle &&
                        <Zoom in={sidebarOptions?.open}>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                sx={{ml: 0, mr: -1.5}}
                                onClick={handleSidebarToggle}
                            >
                                <MenuOpenIcon/>
                            </IconButton>
                        </Zoom>
                    }
                </DrawerHeader>
            }
        </React.Fragment>
    )
};

export default Sidebar;
